/**
 * Token Decimals Cache
 * Prevents redundant contract calls by caching decimals values
 */

class TokenDecimalsCache {
    constructor() {
        this.cache = {};
    }

    /**
     * Get decimals from cache or fetch from contract
     * @param {string} tokenAddress - Token contract address
     * @param {object} web3 - Web3 instance
     * @param {function} contractHelper - Function to create contract instance
     * @returns {Promise<number>} Token decimals
     */
    async getDecimals(tokenAddress, web3, contractHelper) {
        // Return cached value if exists
        if (this.cache[tokenAddress]) {
            console.log(`Using cached decimals for ${tokenAddress}: ${this.cache[tokenAddress]}`);
            return this.cache[tokenAddress];
        }

        try {
            // Fetch from contract
            const contract = contractHelper(tokenAddress, web3);
            const decimals = await contract.methods.decimals().call();
            const decimalsNum = parseInt(decimals);

            // Cache the value
            this.cache[tokenAddress] = decimalsNum;
            console.log(`Fetched and cached decimals for ${tokenAddress}: ${decimalsNum}`);

            return decimalsNum;
        } catch (err) {
            console.error(`Failed to fetch decimals for ${tokenAddress}, defaulting to 18`, err);
            // Default to 18 but don't cache it (allow retry next time)
            return 18;
        }
    }

    /**
     * Manually set decimals (useful for testing or known values)
     * @param {string} tokenAddress - Token contract address
     * @param {number} decimals - Number of decimals
     */
    setDecimals(tokenAddress, decimals) {
        this.cache[tokenAddress] = decimals;
        console.log(`Manually set decimals for ${tokenAddress}: ${decimals}`);
    }

    /**
     * Clear cache for a specific token
     * @param {string} tokenAddress - Token contract address
     */
    clearToken(tokenAddress) {
        delete this.cache[tokenAddress];
        console.log(`Cleared decimals cache for ${tokenAddress}`);
    }

    /**
     * Clear entire cache
     */
    clearAll() {
        this.cache = {};
        console.log("Cleared all decimals cache");
    }

    /**
     * Get all cached values (for debugging)
     */
    getAll() {
        return { ...this.cache };
    }
}

// Export singleton instance
export const decimalsCache = new TokenDecimalsCache();

// Export class for testing
export default TokenDecimalsCache;